import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Scenario {
  id: number;
  type: string;
  title: string;
  description: string;
  difficulty: string;
  nmcCodeRef: string | null;
  rcnRef: string | null;
  answer: string | null;
  keyLearningPoints: string[];
  category: string | null;
  relatedConditionIds: number[];
  relatedMedicationIds: number[];
  createdAt: string;
}

type SForm = Omit<Scenario, "id" | "createdAt">;

const EMPTY: SForm = {
  type: "advice-line",
  title: "",
  description: "",
  difficulty: "intermediate",
  nmcCodeRef: "",
  rcnRef: "",
  answer: "",
  keyLearningPoints: [],
  category: "",
  relatedConditionIds: [],
  relatedMedicationIds: [],
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function SForm({ value, onChange }: { value: SForm; onChange: (v: SForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const arrStr = (a: string[]) => a.join(", ");
  const strArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const numArr = (s: string) => s.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n));

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Type *</Label>
          <Select value={value.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="advice-line">Advice Line</SelectItem>
              <SelectItem value="clinical">Clinical</SelectItem>
              <SelectItem value="ethical">Ethical</SelectItem>
              <SelectItem value="communication">Communication</SelectItem>
              <SelectItem value="medication">Medication</SelectItem>
              <SelectItem value="nmc">NMC</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Difficulty *</Label>
          <Select value={value.difficulty} onValueChange={(v) => set("difficulty", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={(e) => set("title", e.target.value)} placeholder="Scenario title" />
      </div>
      <div>
        <Label>Category</Label>
        <Input value={value.category ?? ""} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Medication Safety" />
      </div>
      <div>
        <Label>Description *</Label>
        <Textarea rows={4} value={value.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the scenario…" />
      </div>
      <div>
        <Label>Suggested answer / guidance</Label>
        <Textarea rows={4} value={value.answer ?? ""} onChange={(e) => set("answer", e.target.value)} />
      </div>
      <div>
        <Label>Key learning points (comma-separated)</Label>
        <Input value={arrStr(value.keyLearningPoints)} onChange={(e) => set("keyLearningPoints", strArr(e.target.value))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>NMC Code ref</Label>
          <Input value={value.nmcCodeRef ?? ""} onChange={(e) => set("nmcCodeRef", e.target.value)} />
        </div>
        <div>
          <Label>RCN ref</Label>
          <Input value={value.rcnRef ?? ""} onChange={(e) => set("rcnRef", e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Related condition IDs (comma-separated)</Label>
        <Input value={value.relatedConditionIds.join(", ")} onChange={(e) => set("relatedConditionIds", numArr(e.target.value))} placeholder="1, 5, 12" />
      </div>
      <div>
        <Label>Related medication IDs (comma-separated)</Label>
        <Input value={value.relatedMedicationIds.join(", ")} onChange={(e) => set("relatedMedicationIds", numArr(e.target.value))} />
      </div>
    </div>
  );
}

export default function AdminScenarios() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Scenario | null>(null);
  const [form, setForm] = useState<SForm>(EMPTY);

  const { data: scenarios = [], isLoading } = useQuery<Scenario[]>({
    queryKey: ["admin", "scenarios"],
    queryFn: () => apiFetch("/api/admin/content/scenarios"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: SForm) =>
      editing
        ? apiFetch(`/api/admin/content/scenarios/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/scenarios", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "scenarios"] }); setDialogOpen(false); toast({ title: editing ? "Scenario updated" : "Scenario created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/scenarios/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "scenarios"] }); toast({ title: "Scenario deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(s: Scenario) {
    setEditing(s);
    setForm({ type: s.type, title: s.title, description: s.description, difficulty: s.difficulty, nmcCodeRef: s.nmcCodeRef, rcnRef: s.rcnRef, answer: s.answer, keyLearningPoints: s.keyLearningPoints, category: s.category, relatedConditionIds: s.relatedConditionIds, relatedMedicationIds: s.relatedMedicationIds });
    setDialogOpen(true);
  }

  const diffColor: Record<string, string> = { beginner: "bg-green-100 text-green-800", intermediate: "bg-yellow-100 text-yellow-800", advanced: "bg-red-100 text-red-800" };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Scenarios"
        description={`${scenarios.length} scenario${scenarios.length !== 1 ? "s" : ""}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New scenario</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Title", "Type", "Difficulty", "Actions"]}
          rows={scenarios.map((s) => [
            <p key="title" className="font-medium text-slate-900 line-clamp-1">{s.title}</p>,
            <Badge key="type" variant="outline" className="text-xs capitalize">{s.type}</Badge>,
            <span key="diff" className={`text-xs px-2 py-0.5 rounded-full font-medium ${diffColor[s.difficulty] ?? "bg-slate-100 text-slate-700"}`}>{s.difficulty}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(s.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit scenario" : "New scenario"}</DialogTitle></DialogHeader>
          <SForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete scenario?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the scenario.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
